import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/system';
import Dialog from '@mui/material/Dialog';
import { Switch } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import SearchIcon from '@mui/icons-material/Search';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import "react-datepicker/dist/react-datepicker.css";
import { scheduleValidation, getValidationSchedule, changeValidationActive, deleteSchedule } from "../ruleService";
import { showSuccessToast, showWarningToast } from "../../validation/ToastContainer";
import { connect } from "react-redux";
import { PopupContext } from "../../index";
import { getDeletePopup } from "../../view/popup/PopupContainer";
import SelectV2 from "../../view/SelectV2";
import ScheduleDeviceTypeField from "./ScheduleDeviceTypeField";
import ScheduleChooseDeviceField from "./ScheduleChooseDeviceField";
import ScheduleSelectMsbField from "./ScheduleSelectMsbField";

const StyledButton = styled('button')({
    '&.disabled': {
        opacity: 0.5,
        pointerEvents: 'none',
    },
});

const WEEK_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const YEAR_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function getDefaultScheduleSelections() {
    const currentDate = new Date();

    return {
        weekDay: WEEK_DAYS[currentDate.getDay()],
        dayOfMonth: String(currentDate.getDate()),
        monthName: YEAR_MONTHS[currentDate.getMonth()]
    };
}

function ScheduleValidateButton({ setSelectedDeviceTypeId, selectedDeviceTypeId, pageData, setMsbId, setDeviceId, deviceId, msbId, pk, userRole }) {
    const { setPopup } = useContext(PopupContext);
    const defaultScheduleSelections = useMemo(() => getDefaultScheduleSelections(), []);
    const [open, setOpen] = useState(false);
    const [viewSchedules, setViewSchedules] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedRecurrenceFrequency, setSelectedRecurrenceFrequency] = useState(1);
    const [selectedPattern, setSelectedPattern] = useState('day');
    const [selectedDays, setSelectedDays] = useState(() => [defaultScheduleSelections.weekDay]);
    const [selectedMonthOption, setSelectedMonthOption] = useState(() => defaultScheduleSelections.dayOfMonth);
    const [selectedYearOption, setSelectedYearOption] = useState(() => defaultScheduleSelections.dayOfMonth);
    const [selectedYearNameOption, setSelectedYearNameOption] = useState(() => defaultScheduleSelections.monthName);
    const [hours, setHours] = useState('1');
    const [minutes, setMinutes] = useState('5');
    const [period, setPeriod] = useState('AM');
    const [validationScheduleResult, setValidationScheduleResult] = useState([]);

    const deviceTypeList = useMemo(() => (
        Array.isArray(pageData?.deviceTypeList) ? pageData.deviceTypeList : []
    ), [pageData?.deviceTypeList]);

    const deviceList = useMemo(() => (
        Array.isArray(pageData?.deviceList) ? pageData.deviceList : []
    ), [pageData?.deviceList]);

    const msbList = useMemo(() => (
        Array.isArray(pageData?.msbList) ? pageData.msbList : []
    ), [pageData?.msbList]);

    const fetchData = async () => {
        const result = await getValidationSchedule(userRole);
        const schedules = Array.isArray(result)
            ? result
            : Object.values(result || {}).filter((item) => item && typeof item === 'object');
        setValidationScheduleResult(schedules);
    };

    useEffect(() => {
        if (viewSchedules) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewSchedules]);

    const resetScheduleForm = useCallback(() => {
        setSelectedRecurrenceFrequency(1);
        setSelectedPattern('day');
        setSelectedDays([defaultScheduleSelections.weekDay]);
        setSelectedMonthOption(defaultScheduleSelections.dayOfMonth);
        setSelectedYearOption(defaultScheduleSelections.dayOfMonth);
        setSelectedYearNameOption(defaultScheduleSelections.monthName);
        setHours('1');
        setMinutes('5');
        setPeriod('AM');
    }, [defaultScheduleSelections.dayOfMonth, defaultScheduleSelections.monthName, defaultScheduleSelections.weekDay]);

    const handleClickOpen = () => {
        resetScheduleForm();
        setViewSchedules(false);
        setSearchQuery('');
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setViewSchedules(false);
        setSearchQuery('');
    };

    const handleViewSchedules = () => {
        setSearchQuery('');
        setViewSchedules(true);
    };

    const isFormValid = Number(selectedDeviceTypeId) > 0 && Number(deviceId) > 0 && Number(msbId) > 0;
    const hourOptions = useMemo(() => (
        Array.from({ length: 12 }, (_, index) => ({
            value: String(index + 1),
            label: `${index + 1} hr.`
        }))
    ), []);
    const minuteOptions = useMemo(() => (
        Array.from({ length: 12 }, (_, index) => {
            const minute = String(index * 5);
            return { value: minute, label: `${minute} min.` };
        })
    ), []);
    const periodOptions = useMemo(() => ([
        { value: 'AM', label: 'AM' },
        { value: 'PM', label: 'PM' }
    ]), []);
    const patternOptions = useMemo(() => ([
        { value: 'day', label: 'day' },
        { value: 'week', label: 'week' },
        { value: 'month', label: 'month' },
        { value: 'year', label: 'year' }
    ]), []);
    const dayOfMonthOptions = useMemo(() => (
        Array.from({ length: 31 }, (_, index) => {
            const day = String(index + 1);
            return { value: day, label: day };
        })
    ), []);
    const yearMonthOptions = useMemo(() => (
        YEAR_MONTHS.map((month) => ({ value: month, label: month }))
    ), []);

    const getWeeklyDaysForSubmit = useCallback(() => {
        if (selectedDays.length > 0) {
            return selectedDays.join(',');
        }

        return defaultScheduleSelections.weekDay;
    }, [defaultScheduleSelections.weekDay, selectedDays]);

    const getRecurrenceSummary = useCallback(({
        recurrencePattern = selectedPattern,
        recurEvery = selectedRecurrenceFrequency,
        selectedWeekDays = selectedDays.join(','),
        selectedMonthDay = selectedMonthOption,
        selectedYearMonth = selectedYearNameOption,
        selectedYearDay = selectedYearOption,
        useScheduleLabel = true
    } = {}) => {
        if (recurrencePattern === 'day') {
            return `${useScheduleLabel ? 'The Validation will occur every' : 'Occur every'} ${recurEvery === 1 ? 'day' : `${recurEvery} days`}`;
        }

        if (recurrencePattern === 'week') {
            const days = selectedWeekDays || getWeeklyDaysForSubmit();
            return `${useScheduleLabel ? 'The Validation will occur every' : 'Occur every'} ${recurEvery === 1 ? 'week' : `${recurEvery} weeks`} on ${days}`;
        }

        if (recurrencePattern === 'month') {
            return `${useScheduleLabel ? 'The Validation will occur on day' : 'Occur on day'} ${selectedMonthDay} ${useScheduleLabel ? 'for every' : 'for every'} ${recurEvery === 1 ? 'month' : `${recurEvery} months`}`;
        }

        return `${useScheduleLabel ? 'The Validation will occur on every' : 'Occur on every'} ${selectedYearMonth} ${selectedYearDay}`;
    }, [
        getWeeklyDaysForSubmit,
        selectedMonthOption,
        selectedPattern,
        selectedRecurrenceFrequency,
        selectedDays,
        selectedYearNameOption,
        selectedYearOption
    ]);

    const handleOnSubmit = async () => {
        if (!isFormValid) {
            showWarningToast('Please complete all required fields before creating the schedule.', 'Validation Schedule');
            return;
        }

        if (selectedPattern === 'week' && selectedDays.length === 0) {
            showWarningToast('Please select at least one day for the weekly schedule.', 'Validation Schedule');
            return;
        }

        const hourNumber = parseInt(hours, 10);
        const normalizedHour = period === 'AM'
            ? (hourNumber === 12 ? 0 : hourNumber)
            : (hourNumber === 12 ? 12 : hourNumber + 12);
        const startTimeDb = new Date();

        startTimeDb.setHours(normalizedHour);
        startTimeDb.setMinutes(parseInt(minutes, 10));
        startTimeDb.setSeconds(0);
        startTimeDb.setMilliseconds(0);

        const validationSchedule = {
            userId: pk,
            userRole: userRole,
            deviceTypeId: Number(selectedDeviceTypeId),
            msbId: Number(msbId),
            deviceId: Number(deviceId),
            startTime: startTimeDb.toISOString(),
            recurrencePattern: selectedPattern,
            recurEvery: Number(selectedRecurrenceFrequency),
            selectedWeekDays: selectedPattern === 'week' ? getWeeklyDaysForSubmit() : null,
            selectedMonthDay: selectedPattern === 'month' ? Number(selectedMonthOption) : null,
            selectedYearMonth: selectedPattern === 'year' ? selectedYearNameOption : null,
            selectedYearDay: selectedPattern === 'year' ? Number(selectedYearOption) : null,
            active: true
        };

        scheduleValidation(validationSchedule)
            .then(() => {
                showSuccessToast(`Validation Schedule is saved`, 'validationSchedule');
                handleClose();
            });
    };

    const findById = useCallback((items, id) => (
        items.find((item) => String(item.id) === String(id))
    ), []);

    const getDeviceTypeName = useCallback((id) => (
        id ? findById(deviceTypeList, id)?.name || '' : ''
    ), [deviceTypeList, findById]);

    const getDeviceName = useCallback((id) => (
        id ? findById(deviceList, id)?.hostName || '' : ''
    ), [deviceList, findById]);

    const getMsbName = useCallback((id) => (
        id ? findById(msbList, id)?.name || '' : ''
    ), [findById, msbList]);

    const handleToggleChange = (scheduleId, currentActive) => {
        const nextActive = !currentActive;

        setValidationScheduleResult((currentSchedules) => currentSchedules.map((schedule) => (
            String(schedule.id) === String(scheduleId)
                ? { ...schedule, active: nextActive }
                : schedule
        )));

        const validationScheduleStatus = {
            pk: parseInt(scheduleId, 10),
            active: nextActive
        };

        changeValidationActive(validationScheduleStatus)
            .then(() => { showSuccessToast(`Validation Schedule is Updated`, 'validationScheduleStatus'); });
    };

    const handleClickDeleteOpen = (id) => {
        setPopup(
            getDeletePopup(
                'Are you sure you want to delete this schedule?',
                'Delete Schedule',
                () => handleDelete(id),
                undefined,
                'Delete Schedule'
            )
        );
    };

    const handleDelete = async (id) => {
        deleteSchedule(id)
            .then(async () => {
                showSuccessToast(`Validation Schedule is Deleted`, 'Delete Schedule');
                await fetchData();
            })
            .catch((error) => {
                console.error('Error deleting schedule:', error);
            });
    };

    const toggleWeekDay = (day) => {
        setSelectedDays((currentDays) => {
            if (currentDays.includes(day)) {
                return currentDays.filter((selectedDay) => selectedDay !== day);
            }

            return WEEK_DAYS.filter((weekDay) => [...currentDays, day].includes(weekDay));
        });
    };

    const getDateTimeFormat = (pDate) => {
        if (!pDate || pDate === "") {
            return "";
        }

        const date = new Date(pDate);
        const yyyy = date.getFullYear();
        const MM = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        let hourValue = date.getHours();
        const minuteValue = String(date.getMinutes()).padStart(2, '0');
        const hh = String(hourValue % 12 || 12).padStart(2, '0');
        const ampm = hourValue >= 12 ? 'PM' : 'AM';

        return `${yyyy}-${MM}-${dd} ${hh}:${minuteValue} ${ampm}`;
    };

    const schedules = useMemo(() => {
        if (!validationScheduleResult.length) {
            return [];
        }

        return validationScheduleResult.map((schedule, index) => ({
            ...schedule,
            key: schedule.id ?? `schedule-${index}`
        }));
    }, [validationScheduleResult]);

    const filteredSchedules = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();

        if (!query) {
            return schedules;
        }

        return schedules.filter((schedule) => {
            const deviceTypeName = getDeviceTypeName(schedule.deviceTypeId);
            const deviceName = getDeviceName(schedule.deviceId);
            const msbName = getMsbName(schedule.msbId);
            const haystack = [
                new Date(schedule.startTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
                schedule.userRole,
                deviceTypeName,
                deviceName,
                msbName,
                schedule.recurrencePattern,
                getRecurrenceSummary({
                    recurrencePattern: schedule.recurrencePattern,
                    recurEvery: schedule.recurEvery,
                    selectedWeekDays: schedule.selectedWeekDays,
                    selectedMonthDay: schedule.selectedMonthDay,
                    selectedYearMonth: schedule.selectedYearMonth,
                    selectedYearDay: schedule.selectedYearDay,
                    useScheduleLabel: false
                }),
                getDateTimeFormat(schedule.lastScheduledTime)
            ].join(' ').toLowerCase();

            return haystack.includes(query);
        });
    }, [getDeviceName, getDeviceTypeName, getMsbName, getRecurrenceSummary, schedules, searchQuery]);

    return (
        <>
            <StyledButton
                type="button"
                className="btn modal-v2-btn modal-v2-btn-primary modal-v2-schedule-trigger"
                onClick={handleClickOpen}
            >
                <ScheduleOutlinedIcon className="oct_icon" fontSize="small" />
                <span>Schedule Validate</span>
            </StyledButton>

            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="schedule-validation-dialog-title"
                aria-describedby="schedule-validation-dialog-description"
                maxWidth={false}
                PaperProps={{
                    className: `modal-v2-root modal-v2-schedule-dialog${viewSchedules ? ' modal-v2-schedule-dialog-list' : ''}`
                }}
                sx={{
                    '& .MuiBackdrop-root': {
                        backdropFilter: 'blur(4px)',
                        background: 'linear-gradient(rgba(2, 6, 23, 0.58), rgba(2, 6, 23, 0.58)), radial-gradient(circle at top, rgba(78, 188, 228, 0.16), transparent 44%)'
                    }
                }}
            >
                <div className="modal-v2-schedule-header">
                    <div className="modal-v2-schedule-header-top">
                        <div className="modal-v2-schedule-header-copy">
                            {viewSchedules ? (
                                <p className="modal-v2-schedule-list-title">Validation Schedule List</p>
                            ) : (
                                <div className="modal-v2-schedule-heading-wrap">
                                    <div className="modal-v2-schedule-heading-icon" aria-hidden="true">
                                        <CalendarMonthOutlinedIcon />
                                    </div>
                                    <div>
                                        <h2 id="schedule-validation-dialog-title" className="modal-v2-schedule-heading">Schedule Validation</h2>
                                        <p className="modal-v2-schedule-subheading">Automate device compliance checks</p>
                                    </div>
                                </div>
                            )}
                        </div>
                        <button type="button" className="close modal-v2-close modal-v2-schedule-close" aria-label="Close" onClick={handleClose}>
                            <CloseIcon fontSize="small" />
                        </button>
                    </div>
                </div>

                <div className="modal-body modal-v2-body modal-v2-schedule-body">
                    {viewSchedules ? (
                        <>
                            <div className="modal-v2-schedule-list-header">
                                <div className="modal-v2-schedule-list-search">
                                    <SearchIcon fontSize="small" />
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(event) => setSearchQuery(event.target.value)}
                                        placeholder="Search schedules..."
                                    />
                                </div>
                            </div>

                            <div className="modal-v2-schedule-list-table-wrap">
                                {filteredSchedules.length === 0 ? (
                                    <div className="modal-v2-schedule-empty-state">No schedules found.</div>
                                ) : (
                                    <table className="modal-v2-schedule-list-table">
                                        <thead>
                                            <tr className="modal-v2-schedule-list-header-row">
                                                <th className="modal-v2-schedule-list-col-start-time">Start Time</th>
                                                <th className="modal-v2-schedule-list-col-scheduled-by">Scheduled By</th>
                                                <th className="modal-v2-schedule-list-col-device-type">Device Type</th>
                                                <th className="modal-v2-schedule-list-col-device">Device</th>
                                                <th className="modal-v2-schedule-list-col-msb">MSB</th>
                                                <th className="modal-v2-schedule-list-col-pattern">Pattern</th>
                                                <th className="modal-v2-schedule-list-col-recurrence">Recurrence</th>
                                                <th className="modal-v2-schedule-list-col-last-execution">Last Execution</th>
                                                <th className="modal-v2-schedule-list-col-enable">Enable</th>
                                                <th className="modal-v2-schedule-list-col-delete">Delete</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredSchedules.map((schedule) => (
                                                <tr key={schedule.key}>
                                                    <td>
                                                        {new Date(schedule.startTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                                    </td>
                                                    <td>{schedule.userRole}</td>
                                                    <td>{getDeviceTypeName(schedule.deviceTypeId)}</td>
                                                    <td>{getDeviceName(schedule.deviceId)}</td>
                                                    <td>{getMsbName(schedule.msbId)}</td>
                                                    <td>{schedule.recurrencePattern}</td>
                                                    <td>
                                                        {getRecurrenceSummary({
                                                            recurrencePattern: schedule.recurrencePattern,
                                                            recurEvery: schedule.recurEvery,
                                                            selectedWeekDays: schedule.selectedWeekDays,
                                                            selectedMonthDay: schedule.selectedMonthDay,
                                                            selectedYearMonth: schedule.selectedYearMonth,
                                                            selectedYearDay: schedule.selectedYearDay,
                                                            useScheduleLabel: false
                                                        })}
                                                    </td>
                                                    <td>{getDateTimeFormat(schedule.lastScheduledTime) || '-'}</td>
                                                    <td className="modal-v2-schedule-list-cell-center">
                                                        <Switch
                                                            checked={Boolean(schedule.active)}
                                                            onChange={() => handleToggleChange(schedule.id, schedule.active)}
                                                            disabled={!schedule.id}
                                                        />
                                                    </td>
                                                    <td className="modal-v2-schedule-list-cell-center">
                                                        <button
                                                            type="button"
                                                            className="modal-v2-schedule-table-delete"
                                                            aria-label="Delete Schedule"
                                                            title="Delete Schedule"
                                                            onClick={() => handleClickDeleteOpen(schedule.id)}
                                                            disabled={!schedule.id}
                                                        >
                                                            <DeleteOutlineOutlinedIcon fontSize="small" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="modal-v2-schedule-form">
                            <div className="card modal-v2-schedule-card modal-v2-schedule-card--time">
                                <div className="card-body">
                                    <div className="modal-v2-schedule-card-heading modal-v2-schedule-section-head">
                                        <span className="modal-v2-schedule-section-icon" aria-hidden="true">
                                            <ScheduleOutlinedIcon fontSize="small" />
                                        </span>
                                        <h3 className="modal-v2-schedule-section-title">Schedule Time</h3>
                                    </div>
                                    <div className="modal-v2-schedule-time-row">
                                        <div className="modal-v2-schedule-time-field">
                                            <label htmlFor="scheduleHour">Hour</label>
                                            <SelectV2
                                                inputId="scheduleHour"
                                                className="modal-v2-schedule-pattern-select"
                                                value={hours}
                                                onChange={setHours}
                                                options={hourOptions}
                                            />
                                        </div>

                                        <div className="modal-v2-schedule-time-field">
                                            <label htmlFor="scheduleMin">Minute</label>
                                            <SelectV2
                                                inputId="scheduleMin"
                                                className="modal-v2-schedule-pattern-select"
                                                value={minutes}
                                                onChange={setMinutes}
                                                options={minuteOptions}
                                            />
                                        </div>

                                        <div className="modal-v2-schedule-time-field">
                                            <label htmlFor="schedulePeriod">Period</label>
                                            <SelectV2
                                                inputId="schedulePeriod"
                                                className="modal-v2-schedule-pattern-select"
                                                value={period}
                                                onChange={setPeriod}
                                                options={periodOptions}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="card modal-v2-schedule-card modal-v2-schedule-card--recurrence">
                                <div className="card-body">
                                    <div className="modal-v2-schedule-card-heading modal-v2-schedule-section-head">
                                        <span className="modal-v2-schedule-section-icon" aria-hidden="true">
                                            <CalendarMonthOutlinedIcon fontSize="small" />
                                        </span>
                                        <h3 className="modal-v2-schedule-section-title">Recurrence Pattern</h3>
                                    </div>

                                    <div className="modal-v2-schedule-recurrence">
                                        <div className="modal-v2-schedule-recurrence-controls">
                                            <div className="modal-v2-schedule-recurrence-control">
                                                <span className="modal-v2-schedule-inline-label">Recur every</span>
                                                <div className="modal-v2-schedule-repeat-fields">
                                                    {selectedPattern !== 'year' && (
                                                        <input
                                                            aria-label="Recur every interval"
                                                            className="oct_form-control-default oct_textalign-center oct_recurrence-frequency-input-v2"
                                                            type="number"
                                                            min="1"
                                                            value={selectedRecurrenceFrequency}
                                                            onChange={(event) => setSelectedRecurrenceFrequency(Math.max(1, parseInt(event.target.value || 1, 10)))}
                                                        />
                                                    )}
                                                    <SelectV2
                                                        inputId="pattern"
                                                        className="modal-v2-schedule-pattern-select"
                                                        value={selectedPattern}
                                                        onChange={setSelectedPattern}
                                                        options={patternOptions}
                                                    />
                                                </div>
                                            </div>

                                            {selectedPattern === 'week' && (
                                                <div className="modal-v2-schedule-weekday-card">
                                                    <span className="modal-v2-schedule-inline-label">On days</span>
                                                    <div className="modal-v2-schedule-weekday-pills" role="group" aria-label="Weekly validation days">
                                                        {WEEK_DAYS.map((day) => {
                                                            const isSelected = selectedDays.includes(day);

                                                            return (
                                                                <button
                                                                    aria-pressed={isSelected}
                                                                    className={`modal-v2-schedule-weekday-pill ${isSelected ? 'is-selected' : ''}`}
                                                                    key={day}
                                                                    onClick={() => toggleWeekDay(day)}
                                                                    type="button"
                                                                >
                                                                    <span className="modal-v2-schedule-weekday-pill-short">{day.slice(0, 3)}</span>
                                                                    <span className="modal-v2-schedule-weekday-pill-full">{day}</span>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                            {selectedPattern === 'month' && (
                                                <div className="modal-v2-schedule-specific-grid">
                                                    <div className="modal-v2-schedule-specific-field">
                                                        <label className="modal-v2-schedule-inline-label" htmlFor="schedule-month-day">On day</label>
                                                        <SelectV2
                                                            inputId="schedule-month-day"
                                                            className="modal-v2-schedule-inline-select"
                                                            value={selectedMonthOption}
                                                            onChange={setSelectedMonthOption}
                                                            options={dayOfMonthOptions}
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            {selectedPattern === 'year' && (
                                                <div className="modal-v2-schedule-specific-grid modal-v2-schedule-specific-grid--year">
                                                    <div className="modal-v2-schedule-specific-field">
                                                        <label className="modal-v2-schedule-inline-label" htmlFor="schedule-year-month">On month</label>
                                                        <SelectV2
                                                            inputId="schedule-year-month"
                                                            className="modal-v2-schedule-inline-select"
                                                            value={selectedYearNameOption}
                                                            onChange={setSelectedYearNameOption}
                                                            options={yearMonthOptions}
                                                        />
                                                    </div>
                                                    <div className="modal-v2-schedule-specific-field">
                                                        <label className="modal-v2-schedule-inline-label" htmlFor="schedule-year-day">On day</label>
                                                        <SelectV2
                                                            inputId="schedule-year-day"
                                                            className="modal-v2-schedule-inline-select"
                                                            value={selectedYearOption}
                                                            onChange={setSelectedYearOption}
                                                            options={dayOfMonthOptions}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="modal-v2-schedule-recurrence-summary">
                                            <InfoOutlinedIcon fontSize="small" />
                                            <span>
                                                {getRecurrenceSummary()}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="card modal-v2-schedule-card">
                                <div className="card-body">
                                    <div className="modal-v2-schedule-card-heading modal-v2-schedule-section-head">
                                        <span className="modal-v2-schedule-section-icon" aria-hidden="true">
                                            <DnsOutlinedIcon fontSize="small" />
                                        </span>
                                        <h3 className="modal-v2-schedule-section-title">Target Configuration</h3>
                                    </div>

                                    <div className="row modal-v2-schedule-grid">
                                        <div className="col-12 col-md-6 modal-v2-schedule-grid-col">
                                            <ScheduleDeviceTypeField
                                                selectType={setSelectedDeviceTypeId}
                                                selectedType={selectedDeviceTypeId}
                                                deviceTypes={deviceTypeList}
                                                setMsbId={setMsbId}
                                                setDeviceId={setDeviceId}
                                            />
                                        </div>
                                        <div className="col-12 col-md-6 modal-v2-schedule-grid-col">
                                            <ScheduleChooseDeviceField
                                                selectedType={selectedDeviceTypeId}
                                                setDeviceId={setDeviceId}
                                                deviceId={deviceId}
                                                devices={deviceList}
                                            />
                                        </div>
                                        <div className="col-12 modal-v2-schedule-grid-col">
                                            <ScheduleSelectMsbField
                                                selectedType={selectedDeviceTypeId}
                                                msbList={msbList}
                                                setMsbId={setMsbId}
                                                msbId={msbId}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-v2-schedule-info-banner" id="schedule-validation-dialog-description">
                                <span className="modal-v2-schedule-info-icon" aria-hidden="true">
                                    <InfoOutlinedIcon fontSize="small" />
                                </span>
                                <div className="modal-v2-schedule-info-copy">
                                    <span className="modal-v2-schedule-info-title">Validation Process:</span> This schedule will automatically run compliance checks against the selected baseline at the specified time.
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {viewSchedules ? (
                    <div className="modal-footer modal-v2-footer modal-v2-schedule-list-footer">
                        <div className="modal-v2-footer-note"></div>
                        <div className="modal-v2-footer-actions">
                            <button type="button" className="btn modal-v2-btn modal-v2-btn-primary modal-v2-schedule-list-back-btn" onClick={() => setViewSchedules(false)}>
                                Back
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="modal-footer modal-v2-footer modal-v2-schedule-footer">
                        <div className="modal-v2-footer-note modal-v2-schedule-required-note">
                            <span className="modal-v2-footer-note-star">*</span> Required fields
                        </div>
                        <div className="modal-v2-footer-actions">
                            <button type="button" className="btn modal-v2-btn modal-v2-btn-outline" onClick={handleClose}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={`btn modal-v2-btn modal-v2-btn-primary${!isFormValid ? ' disabled' : ''}`}
                                onClick={isFormValid ? handleOnSubmit : undefined}
                            >
                                <AddIcon fontSize="small" />
                                Create Schedule
                            </button>
                            <button type="button" className="btn modal-v2-btn modal-v2-btn-primary" onClick={handleViewSchedules}>
                                <FormatListBulletedIcon fontSize="small" />
                                View Schedules
                            </button>
                        </div>
                    </div>
                )}
            </Dialog>
        </>
    );
}

function mapStateToProps(state) {
    return {
        firstName: state.auth.firstName,
        lastName: state.auth.lastName,
        pk: state.auth.pk,
        userRole: state.auth.userRole.name
    };
}

export default connect(mapStateToProps)(ScheduleValidateButton);
